import { apiClient } from '@/services/authApi';
import { endpoints } from '@/services/endpoints';
import type { FormFilingType } from '@/constants/formConstants';

export interface FormListItem {
  id: string;
  filingType: FormFilingType;
  /** Business forms only: display name (e.g. Schedule C, 1065). */
  formName?: string | null;
  referenceId?: string;
  taxYear: number;
  createdBy?: string | null;
  lastModifiedBy?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface FormCreatePayload {
  filingType: FormFilingType;
  taxYear: number;
  /** Optional. INDIVIDUAL create only: prior-year form id to copy personal info from. */
  copyPersonalInfoFromFormId?: string;
  [key: string]: unknown;
}

/** Full form document (individual or business) - backend returns id, filingType + form-specific fields */
export interface FormResponse {
  id: string;
  filingType: FormFilingType;
  [key: string]: unknown;
}

/**
 * List form instances for the authenticated user.
 * Provide either taxYear (exact) or beforeTaxYear (taxYear < value). filingType optional.
 */
export async function listForms(options: {
  taxYear?: number;
  beforeTaxYear?: number;
  filingType?: FormFilingType;
}): Promise<FormListItem[]> {
  const params: Record<string, number | string> = {};
  if (options.beforeTaxYear != null) params.beforeTaxYear = options.beforeTaxYear;
  else if (options.taxYear != null) params.taxYear = options.taxYear;
  if (options.filingType != null) params.filingType = options.filingType;
  const response = await apiClient.get<FormListItem[]>(endpoints.forms.list, {
    params,
  });
  return Array.isArray(response.data) ? response.data : [];
}

/**
 * Create a new individual or business tax form.
 * Body must include filingType, taxYear, and optionally form-specific fields.
 */
export async function createForm(payload: FormCreatePayload): Promise<FormResponse> {
  const response = await apiClient.post<FormResponse>(endpoints.forms.create, payload);
  return response.data;
}

/**
 * Get full form document by id.
 */
export async function getFormById(id: string): Promise<FormResponse> {
  const response = await apiClient.get<FormResponse>(endpoints.forms.getById(id));
  return response.data;
}

/**
 * Update form. Send only fields to update (partial).
 * Do not send id, userId, tenantId, taxYear, filingType.
 */
export async function updateForm(
  id: string,
  payload: Record<string, unknown>
): Promise<FormResponse> {
  const response = await apiClient.put<FormResponse>(endpoints.forms.update(id), payload);
  return response.data;
}

/**
 * Permanently delete a form (individual or business).
 */
export async function deleteForm(id: string): Promise<void> {
  await apiClient.delete(endpoints.forms.delete(id));
}

/** Options for admin form API calls (cross-tenant via tenantId) */
export interface AdminFormOptions {
  tenantId?: string | null;
}

/**
 * Admin: list form instances for a user.
 * Provide either taxYear (exact) or beforeTaxYear (taxYear < value). filingType optional.
 * Pass tenantId for cross-tenant.
 */
export async function listFormsForUser(
  userId: string,
  options: {
    taxYear?: number;
    beforeTaxYear?: number;
    filingType?: FormFilingType;
  } & AdminFormOptions
): Promise<FormListItem[]> {
  const params: Record<string, number | string> = {};
  if (options.beforeTaxYear != null) params.beforeTaxYear = options.beforeTaxYear;
  else if (options.taxYear != null) params.taxYear = options.taxYear;
  if (options.filingType != null) params.filingType = options.filingType;
  if (options.tenantId != null && options.tenantId !== '') params.tenantId = options.tenantId;
  const response = await apiClient.get<FormListItem[]>(endpoints.adminForms.listByUser(userId), {
    params,
  });
  return Array.isArray(response.data) ? response.data : [];
}

/**
 * Admin: create a form for a user.
 * Body: filingType, taxYear, and optionally form-specific fields. Pass tenantId for cross-tenant.
 */
export async function createFormForUser(
  userId: string,
  payload: FormCreatePayload,
  options?: AdminFormOptions
): Promise<FormResponse> {
  const params =
    options?.tenantId != null && options.tenantId !== '' ? { tenantId: options.tenantId } : undefined;
  const response = await apiClient.post<FormResponse>(
    endpoints.adminForms.createForUser(userId),
    payload,
    { params }
  );
  return response.data;
}

/**
 * Admin: get full form document by id for a user.
 */
export async function getFormByIdForUser(
  userId: string,
  formId: string,
  options?: AdminFormOptions
): Promise<FormResponse> {
  const params =
    options?.tenantId != null && options.tenantId !== '' ? { tenantId: options.tenantId } : undefined;
  const response = await apiClient.get<FormResponse>(
    endpoints.adminForms.getById(userId, formId),
    { params }
  );
  return response.data;
}

/**
 * Admin: update form for a user. Send only fields to update (partial).
 */
export async function updateFormForUser(
  userId: string,
  formId: string,
  payload: Record<string, unknown>,
  options?: AdminFormOptions
): Promise<FormResponse> {
  const params =
    options?.tenantId != null && options.tenantId !== '' ? { tenantId: options.tenantId } : undefined;
  const response = await apiClient.put<FormResponse>(
    endpoints.adminForms.update(userId, formId),
    payload,
    { params }
  );
  return response.data;
}

/**
 * Admin: permanently delete a form for a user.
 */
export async function deleteFormForUser(
  userId: string,
  formId: string,
  options?: AdminFormOptions
): Promise<void> {
  const params =
    options?.tenantId != null && options.tenantId !== '' ? { tenantId: options.tenantId } : undefined;
  await apiClient.delete(endpoints.adminForms.delete(userId, formId), { params });
}
